import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Typography,
  Grid,
  Tooltip,
  TextField,
  Tab,
  Tabs,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import SaveIcon from '@mui/icons-material/Save';
import styled from '@emotion/styled';
import moment from 'moment';
import { CancelModal, documentIcon, ToastHelper, ToastType, ToastMessageType, tyrioUI } from '@tyrio/ui-library';
import { WorkOrder, WorkOrderDetailsProps } from './types';
import { useCancelWorkOrder } from '../work-orders/query/cancel-work-order';
import api from '@tyrio/api-factory';
import { DBCustomerOrderItemApi, DBOrderItems, Modules, OrderStatusProps } from '@tyrio/dto';
import { useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../../context/AuthContext';
import { Prisma } from '@prisma/client';
import { AxiosError } from 'axios';

// Styled components matching Transfer Order Details exactly
const Container = styled(Grid)`
  display: flex;
  width: 100%;
  height: 100%;
  padding-top: 16px;
`;

const Wrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  border-bottom: 1;
  width: 100%;
  border-color: divider;
`;

const Header = styled.div`
  display: flex;
  width: 100%;
  height: 65px;
  background: #454f5b;
  padding: 16px;
  justify-content: space-between;
  align-items: center;
  border-radius: 8px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  font-weight: 700;
  font-size: 24px;
  color: white;
`;

const BasicOrderInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const TimeInfo = styled.div`
  font-weight: 400;
  font-size: 14px;
`;

const TabsWrapper = styled(Grid)`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1;
  border-color: divider;
  position: relative;
`;

const TabsStyled = styled(Tabs)({
  borderBottom: '1px solid #e8e8e8',
  '& .MuiTabs-indicator': {
    backgroundColor: '#1976D2',
  },
  '& .MuiButtonBase-root-MuiTab-root': {
    textTransform: 'unset',
  },
  width: '100%',
});

const TabStyled = styled(Tab)({
  textTransform: 'none',
  '&:hover': {
    color: '#1976D2',
    opacity: 1,
  },
  '&.Mui-selected': {
    color: '#1976D2',
  },
  '&.Mui-focusVisible': {
    backgroundColor: '#1976D2',
    color: '#1976D2',
  },
});

const StatusPrinterWrapper = styled.div`
  position: absolute;
  right: 16px;
  display: flex;
  height: 50px;
  align-items: center;
`;

const StatusChip = styled.div<{ color: string }>`
  background: ${(props) => props.color};
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  display: inline-block;
`;

const OrderDetailsContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  max-height: calc(100svh - 259px);
`;

const PaddingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  padding: 0 16px 16px 16px;
`;

const InfoSection = styled.div`
  flex: 1;
  padding: 16px;
  border: 1px solid #dfe3e8;
  border-radius: 8px;
`;

const SectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #212b36;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #f4f6f8;
    padding-bottom: 8px;
    margin-bottom: 8px;
  }
`;

const InfoLabel = styled.span`
  font-weight: 500;
  color: #637381;
  font-size: 14px;
`;

const InfoValue = styled.span`
  font-weight: 400;
  color: #212b36;
  font-size: 14px;
`;

const LineItemsTable = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

const ProductCardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid ${tyrioUI.colors.black.B40};
  padding: 8px 0px;
  padding-left: 16px;
  border-radius: 8px;
`;

const StockWrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding-bottom: 10px;
  align-items: flex-end;
  width: 100%;
  max-width: 280px;
  min-width: 280px;
  justify-content: space-around;
  font-size: 12px;
  font-family: Barlow;
  font-weight: 500;
  gap: 15px;
`;

const TableHeaderWrapper = styled.div`
  width: 100%;
  margin-top: 20px;
  display: flex;
  justify-content: end;
`;

const CardWrapper = styled.div<{
  orderSent?: boolean;
}>`
  display: flex;
  flex-direction: row;
  border-bottom: 1px solid ${tyrioUI.colors.black.B40};
  opacity: ${(prop) => (prop.orderSent ? '50%' : `100%`)};
  &:last-child {
    border: none;
  }
  padding: 8px 0;
`;

const ProductDetailsWrapper = styled.div`
  display: flex;
  justify-content: start;
  flex-direction: column;
  width: 100%;
`;

const ProductPricesWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  min-width: 280px;
  max-width: 280px;
  width: 100%;
  gap: 15px;
`;

const EanIpcSkuWrapper = styled.div`
  display: flex;
  gap: 5px;
  font-size: 12px;
  font-weight: 400;
  max-width: 400px;
  flex-direction: row;
  color: #919eab;

  @media (max-width: 1250px) {
    flex-direction: column;
  }
`;

const TotalWrapper = styled.div`
  background: #f4f6f8;
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 56px;
  gap: 10px;
  width: 90px;
`;

const TableHead = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 50px;
  background: #ecf0f4;
  padding: 16px;
  font-weight: 600;
`;

const LineItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
`;

const LineItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 70px;
  padding: 16px;
  border-bottom: 1px solid #dfe3e8;
`;

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ProductName = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: #212b36;
  margin-bottom: 4px;
`;

const ProductMetadata = styled.div`
  display: flex;
  gap: 8px;
  font-size: 12px;
  font-weight: 400;
  color: #919eab;
`;

const Quantity = styled.div`
  padding: 8px 16px;
  background: #f4f6f8;
  border-radius: 8px;
  font-weight: 500;
  min-width: 50px;
  text-align: center;
`;

const FooterContainer = styled.div`
  padding: 16px;
  padding-bottom: 0;
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  background: white;
  z-index: 22;
  border-radius: 8px;
`;

const Footer = styled.div`
  display: flex;
  height: 80px;
  justify-content: space-between;
  align-items: center;
  border-top: 4px solid #e6e8f0;
`;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabContent = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <Grid
      hidden={value !== index}
      {...other}
      style={{ height: 'calc(100% - 160px)', overflowY: 'auto' }}
    >
      {value === index && (
        <Box sx={{ paddingTop: '16px', height: '100%' }}>{children}</Box>
      )}
    </Grid>
  );
};

const tabProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

// Related Documents Components
const RelatedDocumentsWrapper = styled.div`
  background-color: white;
  height: 100%;
  width: 100%;
  gap: 20px;
  display: flex;
  flex-direction: column;
  padding: 16px;
  max-height: calc(100svh - 259px);
  overflow-y: auto;
`;

const DocumentsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DocumentDetailsWrapper = styled.div`
  border: 1px solid #d2d4d2;
  border-radius: 8px;
  height: 80px;
  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: center;
  padding: 15px;
  justify-content: space-between;
  &:hover {
    border: 1px solid #0990d9;
    cursor: pointer;
  }
`;

const FileIconNameWrapper = styled.div`
  display: flex;
  gap: 20px;
  width: 30%;
  align-items: center;
`;

const ReferenceWrapper = styled.div`
  display: flex;
  justify-content: left;
  align-items: center;
  width: 40%;
`;

// Customer Order Status Colors mapping (same as in CustomerOrderStatusColors)
const getCustomerOrderStatusColor = (status: string): string => {
  const statusColors: Record<string, string> = {
    PENDING: '#FFD700',
    NEW: '#FFFFFF',
    ORDERED: '#9370DB',
    IN_PROCESS: '#1976D2',
    ON_HOLD: '#FFD700',
    SHIPPED: '#A3DFBF',
    DELIVERED: '#38A169',
    COLLECTED: '#38A169',
    FITTED: '#38A169',
    CANCELLED: '#FF0000',
    RETURN_REQUESTED: '#FFA500',
    RETURNED: '#38A169',
    EXCHANGE_REQUESTED: '#FFA500',
    EXCHANGED: '#38A169',
    DROPSHIPPING: '#38A169',
    READY_FOR_SHIPPING: '#1976D2',
  };

  return statusColors[status?.toUpperCase()] || '#919EAB';
};

const DocumentCard = ({
  fileName,
  reference,
  url,
  chipLabel,
}: {
  fileName: string;
  reference: string;
  url: string;
  chipLabel: string;
}) => {
  const backgroundColor = getCustomerOrderStatusColor(chipLabel);
  const textColor = chipLabel?.toUpperCase() === 'NEW' ? '#919EAB' : 'white';
  const hasBorder = chipLabel?.toUpperCase() === 'NEW';

  return (
    <DocumentDetailsWrapper onClick={() => window.open(url, '_blank')}>
      <FileIconNameWrapper>
        <img src={documentIcon} alt="document-icon" />
        <Typography variant="body2">{fileName}</Typography>
      </FileIconNameWrapper>
      <ReferenceWrapper>
        <Typography variant="body2">{reference}</Typography>
      </ReferenceWrapper>
      <Box sx={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
        <Box
          sx={{
            bgcolor: backgroundColor,
            color: textColor,
            border: hasBorder ? '1px solid #919EAB' : 'none',
            px: 1,
            py: 0.5,
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 500,
          }}
        >
          {chipLabel}
        </Box>
      </Box>
    </DocumentDetailsWrapper>
  );
};

export const WorkOrderDetails: React.FC<WorkOrderDetailsProps> = ({
  workOrder,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [workOrderData, setWorkOrderData] = useState(workOrder);
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [internalRemark, setInternalRemark] = useState(
    workOrder.rawData?.customerOrder?.internalRemark || ''
  );
  const [hasChanges, setHasChanges] = useState(false);

  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Update workOrderData when workOrder prop changes
  useEffect(() => {
    setWorkOrderData(workOrder);
    setInternalRemark(workOrder.rawData?.customerOrder?.internalRemark || '');
    setHasChanges(false);
  }, [workOrder]);

  // Track changes to internal remark
  useEffect(() => {
    const originalRemark =
      workOrder.rawData?.customerOrder?.internalRemark || '';
    setHasChanges(internalRemark !== originalRemark);
  }, [internalRemark, workOrder.rawData?.customerOrder?.internalRemark]);

  const iconsStyledProps = {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
    color: 'white',
  };

  const { mutateAsync: cancelWorkOrder, isLoading: isLoadingCancelWorkOrder } =
    useCancelWorkOrder();

  const getStatusChipColor = (status: string): string => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
      case 'NEW':
        return '#FFCC4D';
      case 'IN_PROGRESS':
      case 'PROCESSING':
        return '#1976D2';
      case 'SHIPPED':
      case 'CONFIRMED':
        return '#38A169';
      case 'PAID':
        return '#38A169';
      case 'UNPAID':
      case 'PENDING_PAYMENT':
        return '#FFCC4D';
      case 'CANCELLED':
      case 'FAILED':
        return '#F56565';
      default:
        return '#919EAB';
    }
  };

  // Line items data
  const lineItems = workOrderData.rawData?.lineItems || [];

  const updateOrderMutation = useMutation(
    (request: DBCustomerOrderItemApi['updateOne']['request']) => {
      return api.fetch<DBCustomerOrderItemApi['updateOne']>(
        'update_customer_order',
        {
          id: workOrderData.rawData?.customerOrderId,
          ...request,
        }
      );
    },
    {
      mutationKey: 'update_customer_order_from_work_order',
      onSuccess: () => {
        setHasChanges(false);
        // Update the work order data to reflect the saved changes
        setWorkOrderData((prev) => ({
          ...prev,
          rawData: {
            ...prev.rawData,
            customerOrder: {
              ...prev.rawData?.customerOrder,
              internalRemark: internalRemark,
            },
          },
        }));
        
        // Refresh the work orders table data
        queryClient.invalidateQueries('get_work_orders');
        queryClient.invalidateQueries('get_work_orders_with_branch_info');
        queryClient.invalidateQueries('get_work_orders_paginated');
        
        // Call the onSave callback to refresh the parent component
        onSave?.();
        
        // Show success toast
        ToastHelper.showToast(
          'Work order',
          ToastType.SUCCESS,
          ToastMessageType.UPDATE
        );
      },
      onError: (error: unknown) => {
        console.error('Error saving internal remark:', error);
        if (error instanceof AxiosError) {
          const errorMessage = error.response?.data.error.name;
          ToastHelper.showToast(
            'Work order',
            ToastType.ERROR,
            ToastMessageType.ERROR,
            errorMessage ?? 'An error occurred!'
          );
        } else {
          ToastHelper.showToast(
            'Work order',
            ToastType.ERROR,
            ToastMessageType.ERROR,
            'An error occurred!'
          );
        }
      },
    }
  );

  const handleSave = () => {
    const customerOrderId = workOrderData.rawData?.customerOrderId;
    if (!customerOrderId) {
      console.error('No customer order ID found');
      return;
    }

    const requestBody: DBCustomerOrderItemApi['updateOne']['requestBody'] = {
      orderStatus: workOrderData.rawData?.customerOrder?.orderStatus as unknown as OrderStatusProps,
      deliveryId: workOrderData.rawData?.customerOrder?.deliveryId || '',
      orderType: workOrderData.rawData?.customerOrder?.orderType,
      internalRemark: internalRemark,
      paymentStatus: workOrderData.rawData?.customerOrder?.paymentStatus,
      paymentDetails: workOrderData.rawData?.customerOrder?.paymentDetails && {
        cardInfo: {
          cardType: workOrderData.rawData?.customerOrder?.paymentDetails?.cardInfo?.cardType,
          instalment: workOrderData.rawData?.customerOrder?.paymentDetails?.cardInfo?.instalment,
          transactionId: workOrderData.rawData?.customerOrder?.paymentDetails?.cardInfo?.transactionId,
        },
      },
      address: {
        editedDeliveryAddress: '',
        editedBillingAddress: '',
        editedBy: '',
        updatedAt: '',
      },
      lastEditedBy: {
        lastUpdatedBy: `${user?.firstName} ${user?.lastName}`,
        lastUpdateTime: new Date(),
        module: Modules.UPDATE,
      },
      paymentId: workOrderData.rawData?.customerOrder?.paymentId as string,
      editedItemsMetadata: workOrderData.rawData?.customerOrder?.editedItemsMetadata as Prisma.JsonValue,
      lineItems: workOrderData.rawData?.customerOrder?.lineItems as unknown as DBOrderItems[],
    };

    updateOrderMutation.mutate(requestBody);
  };

  const handleOnCancel = () => {
    setIsCancelModalVisible(true);
  };

  const handleCancelWorkOrder = async () => {
    try {
      await cancelWorkOrder({ workOrder: workOrderData.rawData });
      setIsCancelModalVisible(false);
      onClose(); // Close the details panel after successful cancellation
    } catch (error) {
      console.error('Error canceling work order:', error);
      setIsCancelModalVisible(false);
    }
  };

  const isOrderCancelled =
    workOrderData.externalStatus?.toUpperCase() === 'CANCELLED';

  // Connected orders for related documents
  const connectedOrders: Array<{
    fileName: string;
    url: string;
    chipLabel: string;
    reference: string;
  }> = [
    {
      fileName: 'Customer order',
      url: `/dashboard/customer-orders?searchFilter=orderNumber&searchKeyword=${workOrderData.customerOrderNumber}&selected=${workOrderData.rawData?.customerOrderId}`,
      chipLabel: workOrderData.rawData?.customerOrder?.orderStatus?.status || 'UNKNOWN',
      reference: workOrderData.customerOrderNumber,
    },
  ];

  return (
    <div
      style={{
        background: '#fff',
        width: '100%',
        height: '100%',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {isCancelModalVisible && (
        <CancelModal
          LBAction={() => {
            setIsCancelModalVisible(false);
          }}
          RBAction={handleCancelWorkOrder}
          text="You are about to cancel the work order."
        />
      )}

      <Header>
        <BasicOrderInfoContainer>
          {workOrderData.workOrderNumber}
          <TimeInfo>
            {moment(workOrderData.createdAt).format('DD.MM.YYYY - HH:mm:ss')}
          </TimeInfo>
        </BasicOrderInfoContainer>

        <Grid sx={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <Tooltip placement="top" title="Add to favorites">
            <FavoriteBorderIcon sx={iconsStyledProps} />
          </Tooltip>
          <Tooltip placement="top" title="Close">
            <CloseIcon sx={{ ...iconsStyledProps }} onClick={onClose} />
          </Tooltip>
        </Grid>
      </Header>

      <Container>
        <Wrapper>
          <TabsWrapper>
            <TabsStyled
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
            >
              <TabStyled label="Order" {...tabProps(0)} />
              <TabStyled label="Related documents" {...tabProps(1)} />
            </TabsStyled>
            <StatusPrinterWrapper>
              <StatusChip
                color={getStatusChipColor(workOrderData.externalStatus)}
              >
                {workOrderData.externalStatus}
              </StatusChip>
            </StatusPrinterWrapper>
          </TabsWrapper>

          {/* Order Tab Content */}
          <TabContent value={activeTab} index={0}>
            <OrderDetailsContainer>
              <PaddingWrapper>
                {/* Basic Information */}
                <InfoSection>
                  <SectionTitle>Basic Information</SectionTitle>
                  <InfoRow>
                    <InfoLabel>Work Order Number</InfoLabel>
                    <InfoValue>{workOrderData.workOrderNumber}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>ERP ID</InfoLabel>
                    <InfoValue>
                      {workOrderData.erpId}
                      {workOrderData.erpDocNumber && (
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#919eab',
                            marginTop: '2px',
                          }}
                        >
                          {workOrderData.erpDocNumber}
                        </div>
                      )}
                    </InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Customer Order Number</InfoLabel>
                    <InfoValue>{workOrderData.customerOrderNumber}</InfoValue>
                  </InfoRow>
                </InfoSection>

                {/* Customer Information */}
                <InfoSection>
                  <SectionTitle>Customer Information</SectionTitle>
                  <InfoRow>
                    <InfoLabel>Customer Name</InfoLabel>
                    <InfoValue>{workOrderData.customerTitle}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Branch</InfoLabel>
                    <InfoValue>{workOrderData.branchName}</InfoValue>
                  </InfoRow>
                </InfoSection>
              </PaddingWrapper>

              {/* Line Items Table */}
              <LineItemsTable>
                <TableHeaderWrapper>
                  <StockWrapper>
                    <Typography variant="body2">Price</Typography>
                    <Typography variant="body2">Discount</Typography>
                    <Typography variant="body2">Qty</Typography>
                    <div
                      style={{ width: '90px', display: 'flex', justifyContent: 'center' }}
                    >
                      <Typography variant="body2">Total</Typography>
                    </div>
                  </StockWrapper>
                </TableHeaderWrapper>
                <ProductCardsWrapper
                  style={{
                    borderTop: `1px solid ${tyrioUI.colors.black.B40}`,
                    borderBottom: `1px solid ${tyrioUI.colors.black.B40}`,
                    borderLeft: '0',
                    borderRight: '0',
                    borderRadius: '0',
                  }}
                >
                  {lineItems && lineItems.length > 0 ? (
                    lineItems.map((item: any, index: number) => {
                      const price = item.sellingPrice || 0;
                      const cashDiscount = item.cashDiscount || 0;
                      const discount = cashDiscount * item.quantity;
                      const itemPrice = price * item.quantity - discount;

                      return (
                        <CardWrapper key={index}>
                          <ProductDetailsWrapper>
                            <div style={{ fontWeight: 500, fontSize: '14px', color: '#212b36', marginBottom: '4px' }}>
                              {`${item.brand ? item.brand + ' ' : ''}${
                                item.productName || 'Product'
                              }`}
                            </div>
                            <EanIpcSkuWrapper>
                              <div>EAN: {item.ean || 'N/A'}</div>
                              <div>IPC: {item.manufacturerCode || 'N/A'}</div>
                              <div>SKU: {item.sku || 'N/A'}</div>
                              {item.dot && <div>DOT: {item.dot}</div>}
                            </EanIpcSkuWrapper>
                          </ProductDetailsWrapper>
                          <ProductPricesWrapper>
                            <Typography fontWeight={400}>
                              {price.toLocaleString('de-DE', {
                                style: 'currency',
                                currency: 'EUR',
                                currencyDisplay: 'symbol',
                              })}
                            </Typography>
                            <Typography fontWeight={400}>
                              {discount.toLocaleString('de-DE', {
                                style: 'currency',
                                currency: 'EUR',
                                currencyDisplay: 'symbol',
                              })}
                            </Typography>
                            <Typography fontWeight={600}>{item.quantity || 0}</Typography>
                            <TotalWrapper>
                              <Typography fontWeight={400}>
                                {itemPrice.toLocaleString('de-DE', {
                                  style: 'currency',
                                  currency: 'EUR',
                                  currencyDisplay: 'symbol',
                                })}
                              </Typography>
                            </TotalWrapper>
                          </ProductPricesWrapper>
                        </CardWrapper>
                      );
                    })
                  ) : (
                    <CardWrapper>
                      <ProductDetailsWrapper>
                        <div style={{ fontWeight: 500, fontSize: '14px', color: '#212b36', marginBottom: '4px' }}>
                          No line items available
                        </div>
                      </ProductDetailsWrapper>
                      <ProductPricesWrapper>
                        <Typography fontWeight={400}>€0.00</Typography>
                        <Typography fontWeight={400}>€0.00</Typography>
                        <Typography fontWeight={600}>0</Typography>
                        <TotalWrapper>
                          <Typography fontWeight={400}>€0.00</Typography>
                        </TotalWrapper>
                      </ProductPricesWrapper>
                    </CardWrapper>
                  )}
                </ProductCardsWrapper>
              </LineItemsTable>

              {/* Internal Remark Section */}
              <PaddingWrapper>
                <TextField
                  id="internal-remark"
                  variant="outlined"
                  size="small"
                  multiline
                  rows={3}
                  value={internalRemark}
                  onChange={(e) => setInternalRemark(e.target.value)}
                  disabled={isOrderCancelled}
                  placeholder="Add internal remark..."
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                    },
                  }}
                />
              </PaddingWrapper>
            </OrderDetailsContainer>
          </TabContent>

          {/* Documents Tab Content */}
          <TabContent value={activeTab} index={1}>
            <RelatedDocumentsWrapper>
              {connectedOrders.length > 0 ? (
                <DocumentsWrapper>
                  {connectedOrders.map((order, index) => (
                    <DocumentCard
                      key={index}
                      fileName={order.fileName}
                      reference={order.reference}
                      url={order.url}
                      chipLabel={order.chipLabel}
                    />
                  ))}
                </DocumentsWrapper>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    No related documents available
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 1 }}
                  >
                    Connected customer orders will appear here
                  </Typography>
                </Box>
              )}
            </RelatedDocumentsWrapper>
          </TabContent>
        </Wrapper>
      </Container>

      {/* Footer */}
      <FooterContainer>
        <Footer>
          <Button
            color="error"
            variant="text"
            onClick={handleOnCancel}
            disabled={isOrderCancelled || isLoadingCancelWorkOrder}
          >
            CANCEL ORDER
          </Button>
          <Button
            color="info"
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || isOrderCancelled}
          >
            SAVE
          </Button>
        </Footer>
      </FooterContainer>
    </div>
  );
};
